import { Col, Input, InputNumber, Row, Select } from "antd";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { BasicModel, CategoryModel, MachineSearchFilterModel, MachineSearchSortField } from "../../service/models";
import { loadingAtom } from "../../store/global-atoms";
import { t } from "../../translate";
import BoxContainer from "../containers/box-container";
//import styles from '../styles/filter-components.module.scss';
import { useSecureService } from "../../store/service-atoms";
import MgCharacterSelect from "../atomics/mg-character-select";

type MachineListFilterProps = {
    onChange?: (params: MachineListFilterViewModel) => void;
    children?: any;
};

type MachineListFilterViewModel = {
    searchFilterModel?: MachineSearchFilterModel;
    categoryName?: string | null;
    subCategoryName?: string | null;
    brandName?: string | null;
    sortBy: {
        field: MachineSearchSortField;
        sortAscending: boolean;
    };
};

const MachineListFilter = (props: MachineListFilterProps) => {
    const [allCategories, setAllCategories] = useState<undefined | CategoryModel[]>(undefined);
    const [availableSubcategories, setAvailableSubcategories] = useState<undefined | null | BasicModel[]>(undefined);
    const [availableBrands, setAvailableBrands] = useState<BasicModel[] | undefined>(undefined);
    const [filterModel, setFilterModel] = useState<MachineListFilterViewModel>({
        sortBy: {
            field: MachineSearchSortField.CreationDate,
            sortAscending: false,
        },
    });
    const [loading] = useAtom(loadingAtom);
    const { doGetMachineSubcategory, doGetBrandsByCategory } = useSecureService();
    const { Option } = Select;

    useEffect(() => {
        const getCategoriesAndSubcategories = async () => {
            const result = await doGetMachineSubcategory();
            setAllCategories(result);
        };
        getCategoriesAndSubcategories();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        props.onChange && props.onChange(filterModel);
        // eslint-disable-next-line
    }, [filterModel]);

    const onMachineCategorySelected = async (selectedCategoryName?: string) => {
        const selectedCategory = allCategories?.find((cat) => cat.name === selectedCategoryName);
        if (selectedCategory && selectedCategory.id) {
            const brands = await doGetBrandsByCategory(selectedCategory.id);
            setAvailableBrands(brands);
        } else {
            setAvailableBrands(undefined);
        }
        setAvailableSubcategories(selectedCategory?.subCategories);
        setFilterModel((filt) => {
            return {
                ...filt,
                categoryName: selectedCategory?.name,
                subCategoryName: undefined,
                brandName: undefined,
                searchFilterModel: {
                    ...filt.searchFilterModel,
                    subCategoryId: undefined,
                    brandId: undefined,
                    categoryId: selectedCategory?.id,
                },
            };
        });
    };

    const onSubcategorySelected = async (selectedSubcategoryName?: string) => {
        let selectedSub: BasicModel | undefined;
        allCategories?.find((category) => {
            selectedSub = category.subCategories?.find((subCategory) => {
                return subCategory.name === selectedSubcategoryName;
            });
            return selectedSub !== undefined;
        });
        setFilterModel((filt) => {
            return {
                ...filt,
                subCategoryName: selectedSub?.name,
                searchFilterModel: {
                    ...filt.searchFilterModel,
                    subCategoryId: selectedSub?.id,
                },
            };
        });
    };

    const onBrandSelected = async (selectedBrandName?: string) => {
        let selectedBrand: BasicModel | undefined;
        availableBrands?.find((category) => {
            selectedBrand = availableBrands?.find((br) => {
                return br.name === selectedBrandName;
            });
            return selectedBrand !== undefined;
        });
        setFilterModel((filt) => {
            return {
                ...filt,
                brandName: selectedBrand?.name,
                searchFilterModel: {
                    ...filt.searchFilterModel,
                    brandId: selectedBrand?.id,
                },
            };
        });
    };

    const productionYearChange = (val: any) => {
        let prodYear: number | undefined = undefined;
        if (typeof val === "number") prodYear = val;
        setFilterModel((filter) => {
            return {
                ...filter,
                searchFilterModel: {
                    ...filter.searchFilterModel,
                    productionYearFrom: prodYear,
                    productionYearTo: prodYear,
                },
            };
        });
    };

    const machineIdChange = (val: string) => {
        setFilterModel((filter) => {
            return {
                ...filter,
                searchFilterModel: {
                    ...filter.searchFilterModel,
                    searchKeyword: val,
                },
            };
        });
    };

    return (
        <Row gutter={[8, 8]} style={{ marginBottom: "1rem" }}>
            <Col xs={24} md={4}>
                <BoxContainer shadowless marginless>
                    {t("common.sortBy")}
                    <Select
                        style={{ width: "100%" }}
                        placeholder={t("common.creationDate")}
                        onChange={(val) => {
                            setFilterModel((filter) => {
                                return {
                                    ...filter,
                                    sortBy: {
                                        sortAscending: true,
                                        field: val,
                                    },
                                };
                            });
                        }}
                        getPopupContainer={(trigger: any) => trigger.parentElement}
                    >
                        <Option value="creationDate">{t("common.creationDate")}</Option>
                        <Option value="productionYear">{t("common.productionYear")}</Option>
                    </Select>
                </BoxContainer>
            </Col>
            <Col xs={24} md={20}>
                <BoxContainer shadowless marginless>
                    {t("common.filterBy")}
                    <Row gutter={[12, 12]}>
                        <Col xs={12} md={4}>
                            <MgCharacterSelect 
                                    placeholder={t("machines.add.category")}
                                    value={filterModel.categoryName}
                                    loading={loading}
                                    onSelect={onMachineCategorySelected}
                                    onClear={onMachineCategorySelected}
                                    options={
                                        allCategories?.map((
                                            mc)=>({value:mc.name, label:mc.name})
                                        )}
                            />
                        </Col>
                        <Col xs={12} md={4}>
                             <MgCharacterSelect 
                                placeholder={t("machines.add.subcategory")}
                                value={filterModel?.subCategoryName}
                                    loading={loading}
                                    onSelect={onSubcategorySelected}
                                    onClear={onSubcategorySelected}
                                    options={
                                        availableSubcategories?.map((msc) => (
                                            {value:msc.name , label:msc.name}
                                        ))
                                    }
                            />
                        </Col>
                        <Col xs={12} md={4}>
                            <MgCharacterSelect 
                                placeholder={t("machines.add.brand")}
                                value={filterModel?.brandName}
                                    loading={loading}
                                    onSelect={onBrandSelected}
                                    onClear={onBrandSelected}
                                    options={
                                        availableBrands?.map((msc) => (
                                            {label:msc.name,value:msc.name}
                                        ))
                                    }
                            />      
                        </Col>
                        <Col xs={12} md={3}>
                            <Input
                                allowClear
                                placeholder={t("machines.add.modelName")}
                                value={filterModel.searchFilterModel?.modelName || undefined}
                                onChange={(val) => {
                                    setFilterModel((filter) => {
                                        return {
                                            ...filter,
                                            searchFilterModel: {
                                                ...filter.searchFilterModel,
                                                modelName: val.currentTarget.value,
                                            },
                                        };
                                    });
                                }}
                            />
                        </Col>
                        <Col xs={12} md={3}>
                        <InputNumber
                                type="number"
                                onWheel={(e: any) => e.target.blur()}
                                controls={false}
                                style={{ width: "100%" }}
                                placeholder={t("machines.add.productionYear")}
                                value={filterModel.searchFilterModel?.productionYearFrom || undefined}
                                onKeyDown={(event) => {
                                    const re = /[+-]/;
                                    if (re.test(event.key)) {
                                      event.preventDefault();
                                    }
                                }}
                                onChange={(val) => {
                                    const pattern = /^\+?(0|[1-9]\d*)$/
                                    if(val === null || val === undefined || pattern.test(val.toString()) || val.toString() === '')
                                        productionYearChange(val);
                                }}
                            />
                        </Col>
                        <Col xs={12} md={6}>
                            <InputNumber
                                type="number"
                                style={{ width: "100%" }}
                                placeholder={t("machines.add.machineIdLabel")}
                                value={filterModel.searchFilterModel?.searchKeyword || undefined}
                                onChange={(val) => {
                                    const pattern = /^\+?(0|[1-9]\d*)$/
                                    if(pattern.test(val) || val === '' || val === null || val === undefined)
                                        machineIdChange(val ? val.toString() : "");
                                }}
                            />
                        </Col>
                    </Row>
                </BoxContainer>
            </Col>
        </Row>
    );
};

export default MachineListFilter;
